import { GraphNode } from "../types/graph";
import { Badge } from "../../../ui/common-ui/badge";
import { Separator } from "../../../ui/common-ui/separator";
import { Button } from "../../../ui/common-ui/button";
import {
  Database,
  HardDrive,
  Archive,
  Globe,
  Server,
  Clock,
  AlertTriangle,
  Network,
  Copy,
  Check,
  Cloud,
  MapPin,
  Layers,
  Tag,
  Activity,
  Cpu,
  Shield,
  Building,
  KeyRound,
  Lock,
  Zap,
  HardDriveDownload,
  CheckCircle,
  XCircle,
  Wifi,
} from "lucide-react";
import { useState } from "react";

interface ResourceInspectorProps {
  node: GraphNode;
}

// Format date to readable format
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return "N/A";
  const ts = parseInt(dateString);
  if (!isNaN(ts) && ts > 1000000000000) {
    return new Date(ts).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Get activity level color
const getActivityColor = (activity: number) => {
  if (activity >= 80) return "text-destructive";
  if (activity >= 50) return "text-primary";
  if (activity >= 20) return "text-muted-foreground";
  return "text-muted-foreground/50";
};

export function ResourceInspector({ node }: ResourceInspectorProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const props = node.props || {};
  const resourceType = props.resourceType || "Unknown";

  // Detect cloud provider
  const cloudProvider = props.cloudProvider || props.cloudType || "GCP";
  const isAzure = cloudProvider === 'AZURE';
  const isAws = cloudProvider === 'AWS';
  const isGcp = !isAzure && !isAws;

  // AWS resource category (ec2, s3, rds)
  const resourceCategory = props.resourceCategory || "";

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Get resource icon
  const getResourceIcon = () => {
    if (resourceCategory === 'ec2' || resourceType === "VM") return <Server className="w-6 h-6 text-primary" />;
    if (resourceCategory === 'rds' || resourceType === "Database") return <Database className="w-6 h-6 text-primary" />;
    if (resourceCategory === 's3' || resourceType === "Storage Bucket") return <Archive className="w-6 h-6 text-primary" />;
    return <HardDrive className="w-6 h-6 text-primary" />;
  };

  // Get status color
  const getStatusColor = () => {
    const status = (props.status || props.state || props.instanceState || "").toLowerCase();
    if (status === "running" || status === "available" || status === "active") {
      return "border-primary/30 bg-primary/10 text-primary";
    }
    if (status === "stopped" || status === "terminated" || status === "deallocated") {
      return "border-destructive/40 bg-destructive/10 text-destructive";
    }
    return "border-muted-foreground/30 bg-muted/10 text-muted-foreground";
  };

  const activity = props.activity || 0;
  const status = props.status || props.state || props.instanceState;

  // Format machine type URL to readable name
  const formatMachineType = (machineType: string | null) => {
    if (!machineType) return null;
    const parts = machineType.split("/");
    return parts[parts.length - 1];
  };

  return (
    <div className="space-y-4">
      {/* Resource Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20">
            {getResourceIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-foreground leading-tight break-words">
              {node.label}
            </h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="outline" className="text-[10px]">
                {resourceType}
              </Badge>
              {status && (
                <Badge variant="outline" className={`text-[10px] ${getStatusColor()}`}>
                  {status === "running" || status === "available" ? (
                    <CheckCircle className="w-3 h-3 mr-1" />
                  ) : (
                    <XCircle className="w-3 h-3 mr-1" />
                  )}
                  {status}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      <Separator className="bg-border/50" />

      {/* Stats Badges */}
      <div className="flex flex-wrap gap-2">
        {(props.alertCount || 0) > 0 && (
          <Badge variant="outline" className="text-xs border-destructive/40 bg-destructive/10 text-destructive">
            <AlertTriangle className="w-3 h-3 mr-1" />
            {props.alertCount} Alert{props.alertCount !== 1 ? "s" : ""}
          </Badge>
        )}
        <Badge variant="outline" className="text-xs">
          <Network className="w-3 h-3 mr-1" />
          {props.userCount || 0} User{(props.userCount || 0) !== 1 ? "s" : ""}
        </Badge>
      </div>

      <Separator className="bg-border/50" />

      {/* Cloud Provider */}
      <div className="space-y-1.5">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
          Cloud Provider
        </p>
        <div className="flex items-center gap-2">
          <Cloud className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold">{cloudProvider}</span>
        </div>
      </div>

      {/* Activity */}
      <div className="space-y-1.5">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
          Activity
        </p>
        <div className="flex items-center gap-2">
          <Activity className={`w-3.5 h-3.5 ${getActivityColor(activity)}`} />
          <span className={`text-xs font-semibold ${getActivityColor(activity)}`}>
            {activity}%
          </span>
          {props.lastActivityMessage && (
            <span className="text-xs text-muted-foreground">
              ({props.lastActivityMessage})
            </span>
          )}
        </div>
      </div>

      {/* ========== AWS EC2 Fields ========== */}
      {isAws && resourceCategory === 'ec2' && (
        <>
          {props.instanceId && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Instance ID
              </p>
              <div className="flex items-center gap-2">
                <Server className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all flex-1">
                  {props.instanceId}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 hover:bg-muted/50 shrink-0"
                  onClick={() => handleCopy(props.instanceId, "instanceId")}
                >
                  {copiedField === "instanceId" ? <Check className="w-3 h-3 text-primary" /> : <Copy className="w-3 h-3" />}
                </Button>
              </div>
            </div>
          )}

          {props.instanceArn && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                ARN
              </p>
              <div className="flex items-center gap-2">
                <KeyRound className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all flex-1">
                  {props.instanceArn}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 hover:bg-muted/50 shrink-0"
                  onClick={() => handleCopy(props.instanceArn, "instanceArn")}
                >
                  {copiedField === "instanceArn" ? <Check className="w-3 h-3 text-primary" /> : <Copy className="w-3 h-3" />}
                </Button>
              </div>
            </div>
          )}

          {props.instanceType && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Instance Type
              </p>
              <div className="flex items-center gap-2">
                <Cpu className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.instanceType}</span>
              </div>
            </div>
          )}

          {props.architecture && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Architecture
              </p>
              <div className="flex items-center gap-2">
                <Cpu className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.architecture}</span>
              </div>
            </div>
          )}

          {props.platformDetails && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Platform
              </p>
              <div className="flex items-center gap-2">
                <Layers className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.platformDetails}</span>
              </div>
            </div>
          )}

          {props.availabilityZone && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Availability Zone
              </p>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.availabilityZone}</span>
              </div>
            </div>
          )}

          {props.vpcId && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                VPC ID
              </p>
              <div className="flex items-center gap-2">
                <Network className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30">
                  {props.vpcId}
                </span>
              </div>
            </div>
          )}

          {props.subnetId && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Subnet ID
              </p>
              <div className="flex items-center gap-2">
                <Network className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30">
                  {props.subnetId}
                </span>
              </div>
            </div>
          )}

          {props.privateIpAddress && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Private IP
              </p>
              <div className="flex items-center gap-2">
                <Wifi className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono">{props.privateIpAddress}</span>
              </div>
            </div>
          )}

          {props.publicIpAddress && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Public IP
              </p>
              <div className="flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono">{props.publicIpAddress}</span>
              </div>
            </div>
          )}

          {props.privateDnsName && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Private DNS
              </p>
              <div className="flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs font-mono break-all">{props.privateDnsName}</span>
              </div>
            </div>
          )}

          {props.publicDnsName && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Public DNS
              </p>
              <div className="flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs font-mono break-all">{props.publicDnsName}</span>
              </div>
            </div>
          )}

          {props.securityGroups && props.securityGroups.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Security Groups
              </p>
              <div className="flex flex-wrap gap-1.5">
                {props.securityGroups.map((sg: string, idx: number) => (
                  <Badge key={idx} variant="outline" className="text-[10px] px-2 py-0.5 border-border/50 bg-muted/30">
                    <Shield className="w-2.5 h-2.5 mr-1" />
                    {sg}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {props.keyName && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Key Pair
              </p>
              <div className="flex items-center gap-2">
                <KeyRound className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.keyName}</span>
              </div>
            </div>
          )}

          {props.launchTime && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Launch Time
              </p>
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-medium">{formatDate(props.launchTime)}</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== AWS S3 Fields ========== */}
      {isAws && resourceCategory === 's3' && (
        <>
          {props.bucketName && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Bucket Name
              </p>
              <div className="flex items-center gap-2">
                <Archive className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all flex-1">
                  {props.bucketName}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 hover:bg-muted/50 shrink-0"
                  onClick={() => handleCopy(props.bucketName, "bucketName")}
                >
                  {copiedField === "bucketName" ? <Check className="w-3 h-3 text-primary" /> : <Copy className="w-3 h-3" />}
                </Button>
              </div>
            </div>
          )}

          {props.region && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Region
              </p>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.region}</span>
              </div>
            </div>
          )}

          {props.creationDate && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Created
              </p>
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-medium">{formatDate(props.creationDate)}</span>
              </div>
            </div>
          )}

          {props.isManagedS3 !== undefined && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Managed
              </p>
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-muted-foreground" />
                <Badge variant="outline" className={`text-[10px] ${props.isManagedS3 ? "border-primary/30 bg-primary/10 text-primary" : "border-muted-foreground/30"}`}>
                  {props.isManagedS3 ? "Managed" : "Unmanaged"}
                </Badge>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== AWS RDS Fields ========== */}
      {isAws && resourceCategory === 'rds' && (
        <>
          {props.rdsIdentifierName && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                DB Identifier
              </p>
              <div className="flex items-center gap-2">
                <Database className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all flex-1">
                  {props.rdsIdentifierName}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 hover:bg-muted/50 shrink-0"
                  onClick={() => handleCopy(props.rdsIdentifierName, "rdsIdentifierName")}
                >
                  {copiedField === "rdsIdentifierName" ? <Check className="w-3 h-3 text-primary" /> : <Copy className="w-3 h-3" />}
                </Button>
              </div>
            </div>
          )}

          {props.rdsDBInstanceArn && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                ARN
              </p>
              <div className="flex items-center gap-2">
                <KeyRound className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all flex-1">
                  {props.rdsDBInstanceArn}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 hover:bg-muted/50 shrink-0"
                  onClick={() => handleCopy(props.rdsDBInstanceArn, "rdsDBInstanceArn")}
                >
                  {copiedField === "rdsDBInstanceArn" ? <Check className="w-3 h-3 text-primary" /> : <Copy className="w-3 h-3" />}
                </Button>
              </div>
            </div>
          )}

          {props.rdsEngine && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Engine
              </p>
              <div className="flex items-center gap-2">
                <Zap className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.rdsEngine}</span>
              </div>
            </div>
          )}

          {props.rdsInstanceStatus && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Instance Status
              </p>
              <div className="flex items-center gap-2">
                <Activity className="w-3.5 h-3.5 text-muted-foreground" />
                <Badge variant="outline" className={`text-[10px] ${props.rdsInstanceStatus === 'available' ? "border-primary/30 bg-primary/10 text-primary" : "border-muted-foreground/30"}`}>
                  {props.rdsInstanceStatus}
                </Badge>
              </div>
            </div>
          )}

          {props.rdsAvailabilityZone && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Availability Zone
              </p>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.rdsAvailabilityZone}</span>
              </div>
            </div>
          )}

          {props.rdsInstanceMasterUserName && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Master Username
              </p>
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.rdsInstanceMasterUserName}</span>
              </div>
            </div>
          )}

          {props.rdsMaxAllocatedStorage && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Max Storage
              </p>
              <div className="flex items-center gap-2">
                <HardDriveDownload className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.rdsMaxAllocatedStorage} GB</span>
              </div>
            </div>
          )}

          {props.rdsNetworkType && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Network Type
              </p>
              <div className="flex items-center gap-2">
                <Network className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.rdsNetworkType}</span>
              </div>
            </div>
          )}

          {props.rdsLicenseModel && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                License Model
              </p>
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.rdsLicenseModel}</span>
              </div>
            </div>
          )}

          {props.rdsKmsKeyId && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                KMS Key
              </p>
              <div className="flex items-center gap-2">
                <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs font-mono break-all">{props.rdsKmsKeyId}</span>
              </div>
            </div>
          )}

          {props.rdsCACertificateIdentifier && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                CA Certificate
              </p>
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.rdsCACertificateIdentifier}</span>
              </div>
            </div>
          )}

          {props.rdsMonitoringInterval !== undefined && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Monitoring Interval
              </p>
              <div className="flex items-center gap-2">
                <Activity className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.rdsMonitoringInterval}s</span>
              </div>
            </div>
          )}

          {props.rdsPreferredMaintenanceWindow && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Maintenance Window
              </p>
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.rdsPreferredMaintenanceWindow}</span>
              </div>
            </div>
          )}

          {props.rdsInstanceCreatedTime && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Created
              </p>
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-medium">{formatDate(props.rdsInstanceCreatedTime)}</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== Azure VM Fields ========== */}
      {isAzure && resourceType === "VM" && (
        <>
          {props.size && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Size
              </p>
              <div className="flex items-center gap-2">
                <Cpu className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.size}</span>
              </div>
            </div>
          )}

          {props.osType && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                OS Type
              </p>
              <div className="flex items-center gap-2">
                <Layers className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.osType}</span>
              </div>
            </div>
          )}

          {props.powerState && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Power State
              </p>
              <div className="flex items-center gap-2">
                <Zap className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.powerState.replace('PowerState/', '')}</span>
              </div>
            </div>
          )}

          {props.region && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Region
              </p>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.region}</span>
              </div>
            </div>
          )}

          {props.securityType && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Security Type
              </p>
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.securityType}</span>
              </div>
            </div>
          )}

          {props.resourceGroupName && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Resource Group
              </p>
              <div className="flex items-center gap-2">
                <Building className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all">
                  {props.resourceGroupName}
                </span>
              </div>
            </div>
          )}

          {props.subscriptionId && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Subscription
              </p>
              <div className="flex items-center gap-2">
                <KeyRound className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all">
                  {props.subscriptionId}
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== Azure Database Fields ========== */}
      {isAzure && resourceType === "Database" && (
        <>
          {props.databaseType && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Database Type
              </p>
              <div className="flex items-center gap-2">
                <Database className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.databaseType}</span>
              </div>
            </div>
          )}

          {props.version && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Version
              </p>
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.version}</span>
              </div>
            </div>
          )}

          {props.edition && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Edition
              </p>
              <div className="flex items-center gap-2">
                <Layers className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.edition}</span>
              </div>
            </div>
          )}

          {props.dbStatus && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Status
              </p>
              <div className="flex items-center gap-2">
                <Activity className="w-3.5 h-3.5 text-muted-foreground" />
                <Badge variant="outline" className={`text-[10px] ${props.dbStatus === 'Online' ? "border-primary/30 bg-primary/10 text-primary" : "border-muted-foreground/30"}`}>
                  {props.dbStatus}
                </Badge>
              </div>
            </div>
          )}

          {props.sizeInGb && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Size
              </p>
              <div className="flex items-center gap-2">
                <HardDrive className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.sizeInGb} GB</span>
              </div>
            </div>
          )}

          {props.region && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Region
              </p>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.region}</span>
              </div>
            </div>
          )}

          {props.resourceGroupName && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Resource Group
              </p>
              <div className="flex items-center gap-2">
                <Building className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all">
                  {props.resourceGroupName}
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== Azure Storage Fields ========== */}
      {isAzure && resourceType === "Storage Bucket" && (
        <>
          {props.kind && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Kind
              </p>
              <div className="flex items-center gap-2">
                <Archive className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.kind}</span>
              </div>
            </div>
          )}

          {props.accessTier && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Access Tier
              </p>
              <div className="flex items-center gap-2">
                <Layers className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.accessTier}</span>
              </div>
            </div>
          )}

          {props.skuTier && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                SKU Tier
              </p>
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.skuTier}</span>
              </div>
            </div>
          )}

          {props.region && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Region
              </p>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.region}</span>
              </div>
            </div>
          )}

          {props.publicAccess !== undefined && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Public Access
              </p>
              <div className="flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{String(props.publicAccess)}</span>
              </div>
            </div>
          )}

          {props.resourceGroupName && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Resource Group
              </p>
              <div className="flex items-center gap-2">
                <Building className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all">
                  {props.resourceGroupName}
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== GCP VM Fields ========== */}
      {isGcp && resourceType === "VM" && (
        <>
          {props.machineType && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Machine Type
              </p>
              <div className="flex items-center gap-2">
                <Cpu className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{formatMachineType(props.machineType)}</span>
              </div>
            </div>
          )}

          {props.zone && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Zone
              </p>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.zone}</span>
              </div>
            </div>
          )}

          {props.stackType && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Stack Type
              </p>
              <div className="flex items-center gap-2">
                <Layers className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.stackType}</span>
              </div>
            </div>
          )}

          {props.ip && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                IP Address
              </p>
              <div className="flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono">{props.ip}</span>
              </div>
            </div>
          )}

          {props.projectId && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Project ID
              </p>
              <div className="flex items-center gap-2">
                <Building className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30">
                  {props.projectId}
                </span>
              </div>
            </div>
          )}

          {props.createdAt && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Created
              </p>
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-medium">{formatDate(props.createdAt)}</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== GCP Database Fields ========== */}
      {isGcp && resourceType === "Database" && (
        <>
          {props.instanceType && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Instance Type
              </p>
              <div className="flex items-center gap-2">
                <Database className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.instanceType}</span>
              </div>
            </div>
          )}

          {props.databaseVersion && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Version
              </p>
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.databaseVersion}</span>
              </div>
            </div>
          )}

          {props.tier && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Tier
              </p>
              <div className="flex items-center gap-2">
                <Layers className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.tier}</span>
              </div>
            </div>
          )}

          {props.zone && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Zone
              </p>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.zone}</span>
              </div>
            </div>
          )}

          {props.projectId && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Project ID
              </p>
              <div className="flex items-center gap-2">
                <Building className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30">
                  {props.projectId}
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== GCP Storage Fields ========== */}
      {isGcp && resourceType === "Storage Bucket" && (
        <>
          {props.location && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Location
              </p>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.location}</span>
              </div>
            </div>
          )}

          {props.storageClass && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Storage Class
              </p>
              <div className="flex items-center gap-2">
                <Layers className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold">{props.storageClass}</span>
              </div>
            </div>
          )}

          {props.projectId && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Project ID
              </p>
              <div className="flex items-center gap-2">
                <Building className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30">
                  {props.projectId}
                </span>
              </div>
            </div>
          )}

          {props.createdTime && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Created
              </p>
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-medium">{formatDate(props.createdTime)}</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== Common AWS Account Info ========== */}
      {isAws && props.accountId && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Account ID
          </p>
          <div className="flex items-center gap-2">
            <Building className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30">
              {props.accountId}
            </span>
          </div>
        </div>
      )}

      {/* Tenant */}
      {props.tenantName && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Tenant
          </p>
          <div className="flex items-center gap-2">
            <Building className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium">{props.tenantName}</span>
          </div>
        </div>
      )}

      {/* Last Activity */}
      {props.lastActivity && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Last Seen
          </p>
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium">{formatDate(props.lastActivity)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
